import { type FC, Fragment, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { useSetRecoilState } from 'recoil'

import { NON_EDITABLE_IMAGE_SUBTYPES } from '@messenger/consts'

import { openSwapImageMenuAtom } from '@/atoms/editorAtoms'
import {
  closeQuickActionsForItemIdAtom,
  toggleQuickActionsForItemIdAtom,
} from '@/atoms/itemControlAtoms'
import { Tooltip } from '@/components/global/Tooltip/Tooltip'
import { ImageQuickActions } from '@/components/page/ImageQuickActions/ImageQuickActions'
import { CanvasEnhancementControl } from '@/components/page/Items/ItemControlOverlay/controls/CanvasEnhancementControl'
import {
  QuickActionsIcon,
  QuickActionsMenuContainer,
  SwapIcon,
} from '@/components/page/Items/ItemControlOverlay/ItemControlOverlay.styled'
import type { ImageControlState } from '@/types/itemControls'

type ImageControlsProps = {
  itemId: string
  itemsLocked: boolean
  imageState?: ImageControlState
}

/**
 * Controls specific to image items (rendered in toolbar):
 * - Swap image icon
 * - Revert changes or Show enhancement, on a photo with an accepted
 *   enhancement (`CanvasEnhancementControl`)
 * - Quick actions icon and menu
 */
export const ImageControls: FC<ImageControlsProps> = ({
  itemId,
  itemsLocked,
  imageState,
}) => {
  const { t } = useTranslation()
  const setOpenSwapImageMenu = useSetRecoilState(openSwapImageMenuAtom)
  const setToggleQuickActionsForItemId = useSetRecoilState(
    toggleQuickActionsForItemIdAtom,
  )
  const setCloseQuickActionsForItemId = useSetRecoilState(
    closeQuickActionsForItemIdAtom,
  )

  const handleSwapImage = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation()
      setOpenSwapImageMenu((prev) => !prev)
    },
    [setOpenSwapImageMenu],
  )

  const handleToggleQuickActions = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation()
      setToggleQuickActionsForItemId(itemId)
    },
    [setToggleQuickActionsForItemId, itemId],
  )

  const handleCloseQuickActions = useCallback(() => {
    setCloseQuickActionsForItemId(itemId)
  }, [setCloseQuickActionsForItemId, itemId])

  if (!imageState) {
    return null
  }

  const {
    showQuickActions,
    isProcessingEdit,
    canUseQuickActions,
    quickActions,
    subtype,
  } = imageState

  const isNonPhotoItem = subtype
    ? NON_EDITABLE_IMAGE_SUBTYPES.includes(
        subtype as (typeof NON_EDITABLE_IMAGE_SUBTYPES)[number],
      )
    : false

  // Show quick actions only when items are unlocked
  const showQuickActionsButton =
    !itemsLocked && canUseQuickActions && !isNonPhotoItem

  return (
    <Fragment>
      {/* Swap image is always available regardless of lock state */}
      <Tooltip text={t('itemControls.swap')}>
        <SwapIcon
          onClick={handleSwapImage}
          icon="arrowPath"
          aria-label={t('itemControls.swap')}
          data-testid="overlay-swap-icon"
        />
      </Tooltip>

      {/* A change to the case photo, not to the item: shown whatever the lock */}
      <CanvasEnhancementControl itemId={itemId} />

      {/* Quick actions only available when unlocked */}
      {showQuickActionsButton && (
        <Fragment>
          <Tooltip text={t('itemControls.adjust')}>
            <QuickActionsIcon
              onClick={handleToggleQuickActions}
              icon="adjustmentsHorizontal"
              $disabled={isProcessingEdit}
              aria-label={t('itemControls.adjust')}
              data-testid="overlay-quick-actions-icon"
            />
          </Tooltip>
          {showQuickActions && (
            <QuickActionsMenuContainer>
              <ImageQuickActions
                isOpen={showQuickActions}
                onClose={handleCloseQuickActions}
                actions={quickActions || []}
                position={{ top: 0, left: 0 }}
              />
            </QuickActionsMenuContainer>
          )}
        </Fragment>
      )}
    </Fragment>
  )
}
